/**
 * 🎨 Button Floating Toolbar - Seamless Button Styling
 * 
 * Wiederverwendbare Komponente für Inline-Button-Editing
 * Inspiriert von der TipTap Floating Toolbar, aber button-spezifisch
 * 
 * Features:
 * - Colors: Text Color + Background Color
 * - Dimensions: Height + Border Radius
 * - Effects: Border, Shadow, Gradient (alles in einer Sektion)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useFont } from '../../Design/FontContext';
import { titleFonts, textFonts } from '../../utils/fontsHelper';
import './ButtonFloatingToolbar.css';

function ButtonFloatingToolbar({
    // Required Props
    isEditMode,
    isSelected,
    
    // Style Props
    backgroundColor,
    textColor,
    textSize,
    textSizeMobile,
    textWeight,
    textWeightMobile,
    fontFamily,
    borderRadius,
    borderRadiusMobile,
    height,
    heightMobile,
    width,
    widthMobile,
    
    // Advanced Props
    borderEnabled,
    borderWidth,
    borderColor,
    shadowEnabled,
    shadowIntensity,
    shadowDistance,
    shadowOpacity,
    shadowColor,
    gradientEnabled,
    gradientColor1,
    gradientColor2,
    arrowEnabled,
    arrowDirection,
    arrowIcon, // ✅ NEW: arrow_1, arrow_2, ..., arrow_6
    arrowSize, // ✅ NEW: 12-48px
    arrowInvert, // ✅ NEW: Invert color (filter: invert(1))
    
    // Animation Props
    animationEnabled,
    animationType, // 'pulse' | 'wiggle' | 'gradient-shift'
    
    // Margins
    marginTop,
    marginBottom,
    
    // Alignment Props
    alignment,
    alignmentMobile,
    
    // Link Props
    buttonLink,
    
    // Callbacks
    onStyleChange, // (styles) => void - komplette Style Updates
    colors = {}, // Brand Colors
    onDeselect, // () => void - Callback zum Deselektieren (Outside Click)
    
    // Additional Props
    buttonRef, // Ref zum Button Element für Position-Tracking
    brandFonts = {}, // ✅ NEW: Brand Fonts (globalBigFont, globalSmallFont)
    
    // Responsive
    isMobileLayout = false
}) {
    // 🎨 Toolbar State
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const toolbarRef = useRef(null);
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎯 Font Context für Custom Fonts
    const fonts = useFont();
    
    // 🎯 Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null); // 'colors', 'dimensions', 'effects', 'spacing', 'width', 'typography', 'fontFamily', 'alignment', 'link'
    
    // 🎯 State für Desktop/Mobile Toggle in Width Section
    const [widthDevice, setWidthDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🎯 State für Desktop/Mobile Toggle in Typography Section
    const [typoDevice, setTypoDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🎯 State für Desktop/Mobile Toggle in Alignment Section
    const [alignmentDevice, setAlignmentDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🎯 Synchronisiere Width Toggle mit isMobileLayout
    useEffect(() => {
        setWidthDevice(isMobileLayout ? 'mobile' : 'desktop');
        setTypoDevice(isMobileLayout ? 'mobile' : 'desktop');
        setAlignmentDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);

    // ⚡ OPTIMISTIC UI wird jetzt in EditableButton gehandhabt!
    // Props kommen bereits mit optimistic values von EditableButton
    // Wir brauchen hier nur noch direkte onStyleChange Calls (keine Debouncing mehr!)

    // ✅ Show Toolbar when button is selected
    useEffect(() => {
        if (isEditMode && isSelected) {
            setShowToolbar(true);
            updateToolbarPosition();
        } else {
            setShowToolbar(false);
            setExpandedSection(null);
        }
    }, [isEditMode, isSelected]);

    // ✅ Update Toolbar Position - FIXED im Viewport (wie TipTap Toolbar)
    const updateToolbarPosition = () => {
        // 🎯 FIXED POSITION: Immer 80px von oben, horizontal zentriert
        // ✅ FREEZE POSITION: Wenn expandedSection aktiv ist, Position nicht mehr updaten
        if (expandedSection || isManuallyPositioned) {
            setShowToolbar(true);
            return;
        }
        
        // ✅ EINFACH: Feste Position im Viewport
        const top = 80; // Immer 80px vom Viewport-Top (wie TipTap Toolbar)
        const left = window.innerWidth / 2; // Horizontal zentriert
        
        setToolbarPosition({ top, left });
    };

    // 🎯 Drag Handlers - MUSS VOR early return stehen!
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 300);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 200);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true); // ✅ Mark as manually positioned
    };
    
    // ✅ Drag Event Listeners - MUSS VOR early return stehen!
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);

    // ✅ Update Position on Resize (für Zentrierung)
    useEffect(() => {
        if (!showToolbar || isManuallyPositioned) return;
        
        const handleResize = () => {
            // Position nur bei Resize aktualisieren (für Zentrierung), nicht bei expandedSection
            if (!expandedSection) {
                updateToolbarPosition();
            }
        };
        
        // ✅ Nur Resize Listener (Position ist fix im Viewport, nur Zentrierung bei Resize anpassen)
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [showToolbar, expandedSection, isManuallyPositioned]);

    // ✅ Handle Outside Click - Close toolbar and deselect button
    useEffect(() => {
        if (!showToolbar) return;
        
        const handleClickOutside = (event) => {
            // ✅ Check via closest() - funktioniert auch nach Re-Renders!
            const clickedInToolbar = event.target.closest('.button-floating-toolbar-portal');
            const clickedInButton = buttonRef && buttonRef.current && buttonRef.current.contains(event.target);
            
            // ✅ CRITICAL FIX: Check if clicked in contentEditable span (Button Text Input)
            // Wenn User in Button Text klickt, NICHT schließen!
            const clickedInContentEditable = event.target.closest('[contenteditable="true"]');
            const contentEditableInButton = clickedInContentEditable && buttonRef && buttonRef.current && buttonRef.current.contains(clickedInContentEditable);
            
            // Close only if clicked OUTSIDE of toolbar AND button AND contentEditable text
            if (!clickedInToolbar && !clickedInButton && !contentEditableInButton) {
                // Deselect: Close Toolbar
                setExpandedSection(null);
                
                if (onDeselect) {
                    onDeselect();
                }
            }
        };
        
        // ✅ Use mousedown to catch event BEFORE React's onClick
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showToolbar, onDeselect, buttonRef]);

    // 🚫 Don't render if not in edit mode or not selected
    if (!isEditMode || !isSelected || !showToolbar) {
        return null;
    }

    // 🎨 Active Values (Props sind bereits optimistic von EditableButton!)
    const activeHeight = isMobileLayout ? (heightMobile ?? height ?? 50) : (height ?? 50);
    const activeBorderRadius = isMobileLayout ? (borderRadiusMobile ?? borderRadius ?? 8) : (borderRadius ?? 8);
    
    // 🎨 Active Width (responsive) - Unterstützt Pixel-Werte UND 'full' für 100%
    const activeWidth = isMobileLayout ? (widthMobile ?? width) : width;
    const isFullWidth = activeWidth === 'full';

    // 🎨 Toolbar Content (wird über Portal gerendert)
    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`button-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div 
                className="button-toolbar"
                onMouseDown={(e) => {
                    // ✅ FIX: Stoppe alle mousedown Events innerhalb der Toolbar
                    // Verhindert, dass der Outside-Click-Handler triggert
                    e.stopPropagation();
                }}
            >
                {/* 🎨 MAIN TOOLBAR BUTTONS - oben fixiert */}
                <div className="button-toolbar-row">
                    {/* 🎯 Drag Handle Icon */}
                    <div 
                        className="button-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            // ✅ FIX: Verhindere dass Click-Event die Toolbar schließt
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* 1. Button Colors */}
                    <button
                        onClick={() => setExpandedSection(expandedSection === 'colors' ? null : 'colors')}
                        className={`button-toolbar-btn ${expandedSection === 'colors' ? 'is-active' : ''}`}
                        title="Button Colors (Text & Background)"
                    >
                        <i className="fas fa-palette"></i>
                    </button>

                    {/* 2. Button Style (Size) */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'style' ? null : 'style');
                        }}
                        className={`button-toolbar-btn ${expandedSection === 'style' ? 'is-active' : ''}`}
                        title="Button Style (Height, Radius, Width, Alignment)"
                    >
                        <i className="fas fa-ruler-combined"></i>
                    </button>

                    {/* 3. Spacing (Margins) */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'spacing' ? null : 'spacing');
                        }}
                        className={`button-toolbar-btn ${expandedSection === 'spacing' ? 'is-active' : ''}`}
                        title="Spacing (Margins)"
                    >
                        <i className="fas fa-arrows-alt-v"></i>
                    </button>

                    {/* 4. Button Effects */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'effects' ? null : 'effects');
                        }}
                        className={`button-toolbar-btn ${expandedSection === 'effects' ? 'is-active' : ''} ${(borderEnabled || shadowEnabled || arrowEnabled) ? 'has-feature' : ''}`}
                        title="Button Effects (Border, Shadow & Arrow)"
                    >
                        <i className="fas fa-magic"></i>
                    </button>

                    {/* 5. Link */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'link' ? null : 'link');
                        }}
                        className={`button-toolbar-btn ${expandedSection === 'link' ? 'is-active' : ''} ${buttonLink ? 'has-feature' : ''}`}
                        title="Button Link"
                    >
                        <i className="fas fa-link"></i>
                    </button>
                    
                    {/* 🎯 Vertikale Trennlinie */}
                    <div className="button-toolbar-separator"></div>

                    {/* 6. Typography (Font Size & Weight) */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'typography' ? null : 'typography');
                        }}
                        className={`button-toolbar-btn ${expandedSection === 'typography' ? 'is-active' : ''}`}
                        title="Typography (Font Size & Weight)"
                    >
                        <span style={{ fontSize: '16px', fontWeight: '700' }}>Aa</span>
                    </button>

                    {/* 7. Font Family */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'fontFamily' ? null : 'fontFamily');
                        }}
                        className={`button-toolbar-btn ${expandedSection === 'fontFamily' ? 'is-active' : ''}`}
                        title="Font Family"
                    >
                        <i className="fas fa-font"></i>
                    </button>
                </div>
                
                {/* 🎨 EXPANDABLE SECTIONS - klappen nach unten auf */}
                
                {/* 1️⃣ BUTTON COLORS SECTION */}
                {expandedSection === 'colors' && colors && Object.keys(colors).length > 0 && (
                    <div className="button-expanded-section">
                        {/* Text Color */}
                        <div className="button-color-group">
                            <span className="button-color-label">Text Color</span>
                            <div className="button-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="button-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ buttonTextColor: brandColor });
                                        }}
                                        title={`Text Color ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                        
                        {/* Background Color / Gradient */}
                        <div className="button-color-group">
                            <div className="button-effect-header">
                                <span className="button-color-label">Background Color</span>
                                {/* ✅ Custom Toggle für Gradient */}
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={gradientEnabled || false}
                                        onChange={(e) => {
                                            onStyleChange({ buttonGradientEnabled: e.target.checked });
                                        }}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Gradient</span>
                                </label>
                            </div>
                            
                            {/* Normal Background Color (wenn Gradient deaktiviert) */}
                            {!gradientEnabled && (
                                <div className="button-brand-colors">
                                    {Object.values(colors).map((brandColor, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className="button-brand-color"
                                            style={{ backgroundColor: brandColor }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onStyleChange({ buttonColor: brandColor });
                                            }}
                                            title={`Background Color ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                            
                            {/* Gradient Colors (wenn Gradient aktiviert) */}
                            {gradientEnabled && (
                                <div className="button-gradient-controls">
                                    {/* Color 1 */}
                                    <div className="button-gradient-row">
                                        <span className="button-gradient-label">Color 1</span>
                                        <div className="button-brand-colors button-brand-colors-small">
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className="button-brand-color button-brand-color-small"
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ buttonGradientColor1: brandColor });
                                                    }}
                                                    title={`Gradient Color 1`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Color 2 */}
                                    <div className="button-gradient-row">
                                        <span className="button-gradient-label">Color 2</span>
                                        <div className="button-brand-colors button-brand-colors-small">
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className="button-brand-color button-brand-color-small"
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ buttonGradientColor2: brandColor });
                                                    }}
                                                    title={`Gradient Color 2`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="button-section-label">Button Colors</span>
                    </div>
                )}

                {/* 2️⃣ BUTTON STYLE SECTION (Height, Radius, Width, Alignment) */}
                {expandedSection === 'style' && (
                    <div className="button-expanded-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${widthDevice === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => {
                                    const newDevice = widthDevice === 'desktop' ? 'mobile' : 'desktop';
                                    setWidthDevice(newDevice);
                                    setAlignmentDevice(newDevice);
                                }}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Height */}
                        <div className="button-slider-control">
                            <span className="button-slider-label">Height</span>
                            <input
                                type="range"
                                min="30"
                                max="100"
                                value={activeHeight}
                                onChange={(e) => {
                                    const newHeight = parseInt(e.target.value);
                                    // ⚡ OPTIMISTIC: EditableButton handled lokalen State + Debouncing
                                    if (isMobileLayout) {
                                        onStyleChange({ buttonHeightMobile: newHeight });
                                    } else {
                                        onStyleChange({ buttonHeight: newHeight });
                                    }
                                }}
                                className="button-slider"
                            />
                            <span className="button-slider-value">{activeHeight}px</span>
                        </div>
                        
                        {/* Border Radius */}
                        <div className="button-slider-control">
                            <span className="button-slider-label">Radius</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={activeBorderRadius}
                                onChange={(e) => {
                                    const newRadius = parseInt(e.target.value);
                                    // ⚡ OPTIMISTIC: EditableButton handled lokalen State + Debouncing
                                    if (isMobileLayout) {
                                        onStyleChange({ buttonBorderRadiusMobile: newRadius });
                                    } else {
                                        onStyleChange({ buttonBorderRadius: newRadius });
                                    }
                                }}
                                className="button-slider"
                            />
                            <span className="button-slider-value">{activeBorderRadius}px</span>
                        </div>
                        
                        {/* Width Section */}
                        <div className="button-width-quick" style={{ marginTop: '12px' }}>
                            <button
                                className={`button-width-100 ${(widthDevice === 'desktop' ? width : widthMobile) === 'full' ? 'active' : ''}`}
                                onClick={() => {
                                    if (widthDevice === 'desktop') {
                                        const newWidth = width === 'full' ? 200 : 'full';
                                        onStyleChange({ buttonWidth: newWidth });
                                    } else {
                                        const newWidth = widthMobile === 'full' ? 200 : 'full';
                                        onStyleChange({ buttonWidthMobile: newWidth });
                                    }
                                }}
                                title="Toggle 100% width"
                            >
                                <i className="fas fa-arrows-alt-h"></i> 100%
                            </button>
                        </div>

                        {/* Width Slider (nur wenn NICHT full width) */}
                        {(widthDevice === 'desktop' ? width : widthMobile) !== 'full' && (
                            <div className="typo-control">
                                <label>Width</label>
                                <input
                                    type="range"
                                    min="50"
                                    max="800"
                                    step="10"
                                    value={widthDevice === 'desktop' ? (width ?? 200) : (widthMobile ?? 200)}
                                    onChange={(e) => {
                                        const newWidth = parseInt(e.target.value);
                                        // ⚡ OPTIMISTIC: EditableButton handled lokalen State + Debouncing
                                        if (widthDevice === 'desktop') {
                                            onStyleChange({ buttonWidth: newWidth });
                                        } else {
                                            onStyleChange({ buttonWidthMobile: newWidth });
                                        }
                                    }}
                                    className="typo-slider"
                                />
                                <span className="typo-val">
                                    {widthDevice === 'desktop' ? (width ?? 200) : (widthMobile ?? 200)}px
                                </span>
                            </div>
                        )}
                        
                        {/* Alignment Grid */}
                        <div className="button-alignment-grid" style={{ marginTop: '12px' }}>
                            <button
                                type="button"
                                className={`button-alignment-btn ${(alignmentDevice === 'desktop' ? (alignment || 'left') : (alignmentMobile || 'left')) === 'left' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (alignmentDevice === 'desktop') {
                                        onStyleChange({ buttonAlignment: 'left' });
                                    } else {
                                        onStyleChange({ buttonAlignmentMobile: 'left' });
                                    }
                                }}
                                title="Align Left"
                            >
                                <i className="fas fa-align-left"></i>
                                <span>Left</span>
                            </button>
                            <button
                                type="button"
                                className={`button-alignment-btn ${(alignmentDevice === 'desktop' ? (alignment || 'left') : (alignmentMobile || 'left')) === 'center' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (alignmentDevice === 'desktop') {
                                        onStyleChange({ buttonAlignment: 'center' });
                                    } else {
                                        onStyleChange({ buttonAlignmentMobile: 'center' });
                                    }
                                }}
                                title="Align Center"
                            >
                                <i className="fas fa-align-center"></i>
                                <span>Center</span>
                            </button>
                            <button
                                type="button"
                                className={`button-alignment-btn ${(alignmentDevice === 'desktop' ? (alignment || 'left') : (alignmentMobile || 'left')) === 'right' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (alignmentDevice === 'desktop') {
                                        onStyleChange({ buttonAlignment: 'right' });
                                    } else {
                                        onStyleChange({ buttonAlignmentMobile: 'right' });
                                    }
                                }}
                                title="Align Right"
                            >
                                <i className="fas fa-align-right"></i>
                                <span>Right</span>
                            </button>
                        </div>
                        
                        <span className="button-section-label">Button Style</span>
                    </div>
                )}

                {/* 3️⃣ BUTTON EFFECTS SECTION (Border, Shadow, Arrow) */}
                {expandedSection === 'effects' && (
                    <div className="button-expanded-section button-effects-section">
                        {/* 🔲 BORDER */}
                        <div className="button-effect-group">
                            <div className="button-effect-header">
                                {/* ✅ Custom Toggle für Border */}
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={borderEnabled || false}
                                        onChange={(e) => {
                                            onStyleChange({ buttonBorderEnabled: e.target.checked });
                                        }}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Border</span>
                                </label>
                            </div>
                            
                            {borderEnabled && (
                                <div className="button-effect-controls">
                                    {/* Border Width */}
                                    <div className="button-slider-control">
                                        <span className="button-slider-label">Width</span>
                                        <input
                                            type="range"
                                            min="1"
                                            max="8"
                                            value={borderWidth ?? 2}
                                            onChange={(e) => {
                                                const newWidth = parseInt(e.target.value);
                                                onStyleChange({ buttonBorderWidth: newWidth });
                                            }}
                                            className="button-slider"
                                        />
                                        <span className="button-slider-value">{borderWidth ?? 2}px</span>
                                    </div>
                                    
                                    {/* Border Color */}
                                    <div className="button-brand-colors button-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="button-brand-color button-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ buttonBorderColor: brandColor });
                                                }}
                                                title={`Border Color ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="button-effect-divider" />

                        {/* 🌟 SHADOW */}
                        <div className="button-effect-group">
                            <div className="button-effect-header">
                                {/* ✅ Custom Toggle für Shadow */}
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={shadowEnabled || false}
                                        onChange={(e) => {
                                            onStyleChange({ buttonShadowEnabled: e.target.checked });
                                        }}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Shadow</span>
                                </label>
                            </div>
                            
                            {shadowEnabled && (
                                <div className="button-effect-controls">
                                    {/* Blur */}
                                    <div className="button-slider-control">
                                        <span className="button-slider-label">Blur</span>
                                        <input
                                            type="range"
                                            min="5"
                                            max="50"
                                            value={shadowIntensity ?? 15}
                                            onChange={(e) => {
                                                const newBlur = parseInt(e.target.value);
                                                onStyleChange({ buttonShadowIntensity: newBlur });
                                            }}
                                            className="button-slider"
                                        />
                                        <span className="button-slider-value">{shadowIntensity ?? 15}px</span>
                                    </div>
                                    
                                    {/* Distance */}
                                    <div className="button-slider-control">
                                        <span className="button-slider-label">Distance</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="30"
                                            value={shadowDistance ?? 8}
                                            onChange={(e) => {
                                                const newDistance = parseInt(e.target.value);
                                                onStyleChange({ buttonShadowDistance: newDistance });
                                            }}
                                            className="button-slider"
                                        />
                                        <span className="button-slider-value">{shadowDistance ?? 8}px</span>
                                    </div>
                                    
                                    {/* Opacity */}
                                    <div className="button-slider-control">
                                        <span className="button-slider-label">Opacity</span>
                                        <input
                                            type="range"
                                            min="5"
                                            max="80"
                                            value={shadowOpacity ?? 25}
                                            onChange={(e) => {
                                                const newOpacity = parseInt(e.target.value);
                                                onStyleChange({ buttonShadowOpacity: newOpacity });
                                            }}
                                            className="button-slider"
                                        />
                                        <span className="button-slider-value">{shadowOpacity ?? 25}%</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="button-effect-divider" />

                        {/* ➡️ ARROW */}
                        <div className="button-effect-group">
                            <div className="button-effect-header">
                                {/* ✅ Custom Toggle für Arrow */}
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={arrowEnabled || false}
                                        onChange={(e) => {
                                            onStyleChange({ buttonArrowEnabled: e.target.checked });
                                        }}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Arrow</span>
                                </label>
                            </div>
                            
                            {arrowEnabled && (
                                <div className="button-effect-controls">
                                    {/* Arrow Icon Selection - 3x3 Grid */}
                                    <div className="button-arrow-directions">
                                        <span className="button-slider-label">Icon Style</span>
                                        <div className="button-arrow-icon-grid">
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((iconNum) => (
                                                <button
                                                    key={iconNum}
                                                    type="button"
                                                    className={`button-arrow-icon-btn ${(arrowIcon || 'arrow_1') === `arrow_${iconNum}` ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ buttonArrowIcon: `arrow_${iconNum}` });
                                                    }}
                                                    title={`Arrow Style ${iconNum}`}
                                                >
                                                    <img 
                                                        src={`/img/arrow_${iconNum}.svg`} 
                                                        alt={`Arrow ${iconNum}`}
                                                        className="arrow-preview-img"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Arrow Size */}
                                    <div className="button-slider-control">
                                        <span className="button-slider-label">Size</span>
                                        <input
                                            type="range"
                                            min="12"
                                            max="48"
                                            value={arrowSize ?? 24}
                                            onChange={(e) => {
                                                const newSize = parseInt(e.target.value);
                                                onStyleChange({ buttonArrowSize: newSize });
                                            }}
                                            className="button-slider"
                                        />
                                        <span className="button-slider-value">{arrowSize ?? 24}px</span>
                                    </div>
                                    
                                    {/* Arrow Invert Toggle */}
                                    <div className="button-arrow-options">
                                        <label className="btn-custom-toggle-wrapper btn-custom-toggle-small">
                                            <input
                                                type="checkbox"
                                                checked={arrowInvert || false}
                                                onChange={(e) => {
                                                    onStyleChange({ buttonArrowInvert: e.target.checked });
                                                }}
                                                className="btn-custom-toggle-input"
                                            />
                                            <span className="btn-custom-toggle-slider"></span>
                                            <span className="btn-custom-toggle-label">Invert Color</span>
                                        </label>
                                    </div>
                                    
                                    {/* Arrow Direction */}
                                    <div className="button-arrow-directions">
                                        <span className="button-slider-label">Direction</span>
                                        <div className="button-arrow-grid">
                                            <button
                                                type="button"
                                                className={`button-arrow-btn ${arrowDirection === 'left' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ buttonArrowDirection: 'left' });
                                                }}
                                                title="Arrow Left"
                                            >
                                                <i className="fas fa-arrow-left"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className={`button-arrow-btn ${arrowDirection === 'right' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ buttonArrowDirection: 'right' });
                                                }}
                                                title="Arrow Right"
                                            >
                                                <i className="fas fa-arrow-right"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className={`button-arrow-btn ${arrowDirection === 'up' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ buttonArrowDirection: 'up' });
                                                }}
                                                title="Arrow Up"
                                            >
                                                <i className="fas fa-arrow-up"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className={`button-arrow-btn ${arrowDirection === 'down' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ buttonArrowDirection: 'down' });
                                                }}
                                                title="Arrow Down"
                                            >
                                                <i className="fas fa-arrow-down"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="button-effect-divider" />

                        {/* ✨ ANIMATION */}
                        <div className="button-effect-group">
                            <div className="button-effect-header">
                                {/* ✅ Custom Toggle für Animation */}
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={animationEnabled || false}
                                        onChange={(e) => {
                                            // ✅ Wenn aktiviert, setze auch Standard-Animation 'pulse'
                                            if (e.target.checked) {
                                                onStyleChange({ 
                                                    buttonAnimationEnabled: true,
                                                    buttonAnimationType: animationType || 'pulse' // Default to pulse
                                                });
                                            } else {
                                                onStyleChange({ buttonAnimationEnabled: false });
                                            }
                                        }}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Animation</span>
                                </label>
                            </div>
                            
                            {animationEnabled && (
                                <div className="button-effect-controls">
                                    {/* Animation Types */}
                                    <div className="button-animation-types">
                                        <span className="button-slider-label">Animation Type</span>
                                        <div className="button-animation-grid">
                                            {/* Pulse Animation */}
                                            <button
                                                type="button"
                                                className={`button-animation-btn ${animationType === 'pulse' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('🎬 Animation changed to: pulse');
                                                    onStyleChange({ buttonAnimationType: 'pulse' });
                                                }}
                                                title="Pulse Animation (subtle breathing)"
                                            >
                                                <div className="animation-preview pulse-preview">
                                                    <div className="preview-icon"></div>
                                                </div>
                                                <span>Pulse</span>
                                            </button>
                                            
                                            {/* Wiggle Animation */}
                                            <button
                                                type="button"
                                                className={`button-animation-btn ${animationType === 'wiggle' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ buttonAnimationType: 'wiggle' });
                                                }}
                                                title="Wiggle Animation (subtle shake)"
                                            >
                                                <div className="animation-preview wiggle-preview">
                                                    <div className="preview-icon"></div>
                                                </div>
                                                <span>Wiggle</span>
                                            </button>
                                            
                                            {/* Gradient Shift - Only if gradient enabled */}
                                            {gradientEnabled && (
                                                <button
                                                    type="button"
                                                    className={`button-animation-btn ${animationType === 'gradient-shift' ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ buttonAnimationType: 'gradient-shift' });
                                                    }}
                                                    title="Gradient Shift (animated gradient)"
                                                >
                                                    <div className="animation-preview gradient-preview">
                                                        <div className="preview-icon gradient-icon"></div>
                                                    </div>
                                                    <span>Gradient</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="button-section-label">Button Effects</span>
                    </div>
                )}

                {/* 4️⃣ SPACING SECTION (Margins) */}
                {expandedSection === 'spacing' && (
                    <div className="button-expanded-section">
                        {/* Margin Top */}
                        <div className="button-slider-control">
                            <span className="button-slider-label">Top</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={marginTop ?? 30}
                                onChange={(e) => {
                                    const newMarginTop = parseInt(e.target.value);
                                    onStyleChange({ buttonMarginTop: newMarginTop });
                                }}
                                className="button-slider"
                            />
                            <span className="button-slider-value">{marginTop ?? 30}px</span>
                        </div>
                        
                        {/* Margin Bottom */}
                        <div className="button-slider-control">
                            <span className="button-slider-label">Bottom</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={marginBottom ?? 0}
                                onChange={(e) => {
                                    const newMarginBottom = parseInt(e.target.value);
                                    onStyleChange({ buttonMarginBottom: newMarginBottom });
                                }}
                                className="button-slider"
                            />
                            <span className="button-slider-value">{marginBottom ?? 0}px</span>
                        </div>
                        
                        <span className="button-section-label">Spacing</span>
                    </div>
                )}

                {/* 5️⃣ TYPOGRAPHY SECTION (Font Size & Font Weight) */}
                {expandedSection === 'typography' && (
                    <div className="button-expanded-section button-typography-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${typoDevice === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setTypoDevice(typoDevice === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>

                        {/* Font Size */}
                        <div className="typo-control">
                            <label>Size</label>
                            <input
                                type="range"
                                min="10"
                                max="32"
                                value={typoDevice === 'desktop' ? (textSize ?? 18) : (textSizeMobile ?? 16)}
                                onChange={(e) => {
                                    const newSize = parseInt(e.target.value);
                                    if (typoDevice === 'desktop') {
                                        onStyleChange({ buttonTextSize: newSize });
                                    } else {
                                        onStyleChange({ buttonTextSizeMobile: newSize });
                                    }
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">
                                {typoDevice === 'desktop' ? (textSize ?? 18) : (textSizeMobile ?? 16)}px
                            </span>
                        </div>

                        {/* Font Weight */}
                        <div className="typo-control">
                            <label>Weight</label>
                            <input
                                type="range"
                                min="300"
                                max="900"
                                step="100"
                                value={typoDevice === 'desktop' ? (textWeight ?? '600') : (textWeightMobile ?? '600')}
                                onChange={(e) => {
                                    const newWeight = e.target.value;
                                    if (typoDevice === 'desktop') {
                                        onStyleChange({ buttonTextWeight: newWeight });
                                    } else {
                                        onStyleChange({ buttonTextWeightMobile: newWeight });
                                    }
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">
                                {typoDevice === 'desktop' ? (textWeight ?? '600') : (textWeightMobile ?? '600')}
                            </span>
                        </div>
                        
                        <span className="button-section-label">Typography</span>
                    </div>
                )}

                {/* 6️⃣ FONT FAMILY SECTION - Eigener Toggle (wie bei TipTap) */}
                {expandedSection === 'fontFamily' && (() => {
                    console.log('🔍 Font Family Section opened - Current fontFamily:', fontFamily);
                    return (
                    <div className="button-expanded-section">
                        <div className="tiptap-font-family-list">
                            {/* ⭐ BRAND FONTS ZUERST - Mit Badge */}
                            {brandFonts?.globalBigFont && (
                                <button
                                    key="brand-big-font"
                                    type="button"
                                    className={`tiptap-font-family-option brand-font ${fontFamily === brandFonts.globalBigFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: brandFonts.globalBigFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Button Font Family clicked (Brand Big):', brandFonts.globalBigFont);
                                        onStyleChange({ buttonFontFamily: brandFonts.globalBigFont });
                                        console.log('✅ Font change sent to onUpdate');
                                    }}
                                    title={`${brandFonts.globalBigFont} (Brand Headline Font)`}
                                >
                                    ⭐ {brandFonts.globalBigFont}
                                </button>
                            )}
                            
                            {brandFonts?.globalSmallFont && brandFonts.globalSmallFont !== brandFonts.globalBigFont && (
                                <button
                                    key="brand-small-font"
                                    type="button"
                                    className={`tiptap-font-family-option brand-font ${fontFamily === brandFonts.globalSmallFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: brandFonts.globalSmallFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Button Font Family clicked (Brand Small):', brandFonts.globalSmallFont);
                                        onStyleChange({ buttonFontFamily: brandFonts.globalSmallFont });
                                        console.log('✅ Font change sent to onUpdate');
                                    }}
                                    title={`${brandFonts.globalSmallFont} (Brand Body Font)`}
                                >
                                    ⭐ {brandFonts.globalSmallFont}
                                </button>
                            )}
                            
                            {/* Divider nach Brand Fonts */}
                            {(brandFonts?.globalBigFont || brandFonts?.globalSmallFont) && (
                                <div style={{
                                    height: '1px',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    margin: '8px 0'
                                }}></div>
                            )}
                            
                            {/* Custom Fonts - gefiltert (ohne Brand Fonts) */}
                            {fonts.customFonts && fonts.customFonts.length > 0 && (
                                <>
                                    {fonts.customFonts
                                        .filter(customFont => 
                                            customFont.name !== brandFonts?.globalBigFont && 
                                            customFont.name !== brandFonts?.globalSmallFont
                                        )
                                        .map((customFont, index) => (
                                        <button
                                            key={`custom-${index}`}
                                            type="button"
                                            className={`tiptap-font-family-option ${fontFamily === customFont.name ? 'is-active' : ''}`}
                                            style={{ fontFamily: customFont.name }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log('🎨 Button Font Family clicked:', customFont.name);
                                                onStyleChange({ buttonFontFamily: customFont.name });
                                                console.log('✅ Font change sent to onUpdate');
                                            }}
                                            title={customFont.name}
                                        >
                                            {customFont.name}
                                        </button>
                                    ))}
                                </>
                            )}

                            {/* Title Fonts - gefiltert (ohne Brand Fonts) */}
                            {titleFonts
                                .filter(font => 
                                    font !== brandFonts?.globalBigFont && 
                                    font !== brandFonts?.globalSmallFont
                                )
                                .map((font, index) => {
                                const isActive = fontFamily === font;
                                return (
                                <button
                                    key={`title-${index}`}
                                    type="button"
                                    className={`tiptap-font-family-option ${isActive ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Button Font Family clicked:', font);
                                        onStyleChange({ buttonFontFamily: font });
                                        console.log('✅ Font change sent to onUpdate');
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                                );
                            })}

                            {/* Text Fonts - gefiltert (ohne Brand Fonts) */}
                            {textFonts
                                .filter(font => 
                                    font !== brandFonts?.globalBigFont && 
                                    font !== brandFonts?.globalSmallFont
                                )
                                .map((font, index) => (
                                <button
                                    key={`text-${index}`}
                                    type="button"
                                    className={`tiptap-font-family-option ${fontFamily === font ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Button Font Family clicked:', font);
                                        onStyleChange({ buttonFontFamily: font });
                                        console.log('✅ Font change sent to onUpdate');
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                            ))}
                        </div>
                        <span className="button-section-label">Font Family</span>
                    </div>
                    );
                })()}

                {/* 7️⃣ LINK SECTION */}
                {expandedSection === 'link' && (
                    <div className="button-expanded-section button-link-section">
                        {/* Link Input */}
                        <div className="button-link-input-wrapper">
                            <label className="button-link-label">
                                <i className="fas fa-link"></i> Button Link
                            </label>
                            <input
                                type="text"
                                className="button-link-input"
                                value={buttonLink || ''}
                                onChange={(e) => {
                                    onStyleChange({ buttonLink: e.target.value });
                                }}
                                placeholder="https://example.com"
                                autoFocus
                            />
                            
                            {/* Link Info */}
                            <div className="button-link-info">
                                <i className="fas fa-info-circle"></i>
                                <span>Opens in new tab</span>
                            </div>
                            
                            {/* Clear Link Button */}
                            {buttonLink && (
                                <button
                                    type="button"
                                    className="button-link-clear"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ buttonLink: '' });
                                    }}
                                    title="Remove Link"
                                >
                                    <i className="fas fa-times"></i> Clear Link
                                </button>
                            )}
                        </div>
                        
                        <span className="button-section-label">Link</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default ButtonFloatingToolbar;
